import React from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import CustomTypography from 'components/Fileds/CustomTypography';
import { useGetRecipeByFoodItemQuery } from 'pages/inventory/services';

const IngredientsSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
    <Box>
      <Skeleton variant="text" width={180} height={26} sx={{ mb: 1.5, borderRadius: '4px' }} />
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}
      >
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
            <TableRow>
              <TableCell sx={{ py: 1.5, width: '65%' }}>
                <Skeleton variant="text" width={120} height={20} />
              </TableCell>
              <TableCell align="right" sx={{ py: 1.5, width: '35%' }}>
                <Skeleton variant="text" width={70} height={20} sx={{ ml: 'auto' }} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4].map((item) => (
              <TableRow key={item} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={{ py: 1.5 }}>
                  <Skeleton variant="text" width={`${50 + (item % 3) * 15}%`} height={22} />
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5 }}>
                  <Skeleton variant="text" width={60} height={22} sx={{ ml: 'auto' }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </Box>
);

const ViewIngredientsModal = ({ open, onClose, recipe }) => {
  const theme = useTheme();

  // Fetch full recipe details (including addons) by food item ID
  const { data: recipeDetailsResp, isFetching } = useGetRecipeByFoodItemQuery(
    recipe?.id,
    { skip: !open || !recipe?.id }
  );

  const mainIngredients = recipeDetailsResp?.data?.ingredients || [];
  const addonIngredients = recipeDetailsResp?.data?.addonIngredients || [];
  const foodItem = recipeDetailsResp?.data?.foodItem;

  // Helper to find the addon name from groups
  const getAddonName = (addonId) => {
    if (!foodItem?.groups) return `Add-on #${addonId}`;
    for (const group of foodItem.groups) {
      if (group.add_ons) {
        const found = group.add_ons.find(addon => String(addon.id) === String(addonId));
        if (found) return found.name;
      }
    }
    return `Add-on #${addonId}`;
  };

  // Group addon ingredients by addon_id
  const addonGroups = {};
  addonIngredients.forEach(ing => {
    if (!addonGroups[ing.addon_id]) {
      addonGroups[ing.addon_id] = [];
    }
    addonGroups[ing.addon_id].push(ing);
  });

  const hasIngredients = mainIngredients.length > 0 || addonIngredients.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', padding: '10px', color: 'black' } }}
    >
      <Box className="d-flex align-items-center justify-content-between" sx={{ m: 0, p: 2 }}>
        <CustomTypography
          variant="h5"
          label={recipe ? `Ingredients for ${recipe.name}` : 'Ingredients List'}
          sx={{ fontSize: '20px', fontWeight: 700, color: theme.palette.typographyLabel.main, mb: 0 }}
        />
        <IconButton aria-label="close" onClick={onClose} sx={{ color: 'grey.500' }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
        {isFetching ? (
          <IngredientsSkeleton />
        ) : !hasIngredients ? (
          <CustomTypography
            label="No ingredients configured for this recipe."
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Main Item Ingredients Section */}
            {mainIngredients.length > 0 && (
              <Box>
                <CustomTypography
                  label="Main Item Ingredients"
                  sx={{ fontSize: '15px', fontWeight: 700, mb: 1.5, color: '#374151' }}
                />
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                  <Table size="small" aria-label="main ingredients table">
                    <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Ingredient Name</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>Quantity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mainIngredients.map((ing, idx) => (
                        <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ py: 1.5, fontWeight: 500 }}>
                            {ing.inventoryItem?.name || 'N/A'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, fontWeight: 500 }}>
                            {parseFloat(ing.qty).toFixed(2)} {ing.unit}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Addon Ingredients Sections */}
            {Object.keys(addonGroups).map((addonId) => {
              const ingredientsList = addonGroups[addonId];
              return (
                <Box key={addonId}>
                  <CustomTypography
                    label={`${getAddonName(addonId)} (Add-on)`}
                    sx={{ fontSize: '15px', fontWeight: 700, mb: 1.5, color: '#374151', textTransform: 'capitalize' }}
                  />
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
                    <Table size="small" aria-label={`addon ${addonId} ingredients table`}>
                      <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Ingredient Name</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>Quantity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ingredientsList.map((ing, idx) => (
                          <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ py: 1.5, fontWeight: 500 }}>
                              {ing.inventoryItem?.name || 'N/A'}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5, fontWeight: 500 }}>
                              {parseFloat(ing.qty).toFixed(2)} {ing.unit}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewIngredientsModal;
